import { Injectable } from '@angular/core';
import { PriceListModel } from './price-list-data.service';

/**
 * Mirrors the backend priceListIntegration() logic from folderInvoicesExportService
 * so that the MPC shown in the invoice preview is the same value the export writes.
 *
 * Two-phase lookup:
 *   1. Resolve the article code (PriceList.productCode) for a line item by
 *      matching barcode → product code → product name.
 *   2. Once the article code is known, MPC is ALWAYS pulled from the price list
 *      entry for that article code. MPC never comes from the invoice.
 *
 * Keep this in sync with:
 *   amplify/backend/function/folderInvoicesExportService/src/index.py
 */
@Injectable({ providedIn: 'root' })
export class PriceListLookupService {
  private byBarcode = new Map<string, PriceListModel>();
  private byCode = new Map<string, PriceListModel>();
  private byName = new Map<string, PriceListModel>();
  private byArticleCode = new Map<string, PriceListModel>();
  private cachedAllItems: PriceListModel[] = [];
  private loadedForClient: string | null = null;

  /** Barcodes only ever differ by formatting, so compare on digits alone. */
  private normalizeBarcode(value: string | undefined | null): string {
    if (!value) return '';
    return String(value).replace(/\D/g, '');
  }

  /**
   * True/False for a GTIN-8/12/13/14 mod-10 check digit, null if not a GTIN.
   * null means "not a checkable barcode" (e.g. a short supplier article code)
   * and must not be treated as a failure.
   */
  private isValidGtin(value: string | undefined | null): boolean | null {
    const digits = this.normalizeBarcode(value);
    if (![8, 12, 13, 14].includes(digits.length)) return null;
    const nums = digits.split('').map((d) => Number(d));
    const body = nums.slice(0, -1).reverse();
    const total = body.reduce((acc, d, i) => acc + d * (i % 2 === 0 ? 3 : 1), 0);
    return (10 - (total % 10)) % 10 === nums[nums.length - 1];
  }

  /**
   * Barcodes one vision-style slip away from the value that was read: an
   * adjacent digit swap or a single misread digit.
   */
  private barcodeRepairCandidates(value: string): string[] {
    const d = this.normalizeBarcode(value);
    if (!d) return [];
    const out = new Set<string>();
    for (let i = 0; i < d.length - 1; i++) {
      out.add(d.slice(0, i) + d[i + 1] + d[i] + d.slice(i + 2));
    }
    for (let i = 0; i < d.length; i++) {
      for (const c of '0123456789') {
        if (c !== d[i]) out.add(d.slice(0, i) + c + d.slice(i + 1));
      }
    }
    out.delete(d);
    return Array.from(out);
  }

  /**
   * Correct a check-digit failure when the price list leaves no ambiguity.
   * Accepts a candidate only when it is a valid GTIN, present in the price list,
   * and the ONLY such candidate — a confidently wrong barcode is worse than an
   * obviously suspect one.
   */
  private repairBarcode(value: string): string | null {
    const hits = this.barcodeRepairCandidates(value).filter(
      (c) => this.byBarcode.has(c) && this.isValidGtin(c) === true
    );
    return hits.length === 1 ? hits[0] : null;
  }

  /**
   * Product codes are compared without separators, case or leading zeros.
   * Leading zeros are dropped because the price list stores '0001' while an
   * invoice usually prints '1' for the same article.
   */
  private normalizeProductCode(value: string | undefined | null): string {
    if (!value) return '';
    const code = String(value).replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    const stripped = code.replace(/^0+/, '');
    return stripped || code;
  }

  /**
   * Collapse case, accents and punctuation so names compare reliably. Bosnian
   * product names reach us with and without diacritics depending on the OCR
   * engine ('Ožujsko' vs 'Ozujsko'), so accents are folded away.
   */
  private normalizeProductName(value: string | undefined | null): string {
    if (!value) return '';
    return String(value)
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  get isLoaded(): boolean {
    return this.loadedForClient !== null;
  }

  get size(): number {
    return this.byName.size;
  }

  /** All indexed price list entries, for the article-code dropdown. */
  get allItems(): PriceListModel[] {
    return this.cachedAllItems;
  }

  /**
   * Index a price list for O(1) lookups. The first row wins for a duplicated
   * key: the source price lists repeat a product once per barcode, so later
   * rows carry the same price anyway.
   */
  buildIndex(items: PriceListModel[], clientID: string): void {
    this.byBarcode = new Map();
    this.byCode = new Map();
    this.byName = new Map();
    this.byArticleCode = new Map();

    for (const item of items || []) {
      if (!item) continue;
      const barcode = this.normalizeBarcode(item.barcode);
      if (barcode && !this.byBarcode.has(barcode)) this.byBarcode.set(barcode, item);
      const code = this.normalizeProductCode(item.productCode);
      if (code && !this.byCode.has(code)) this.byCode.set(code, item);
      const name = this.normalizeProductName(item.productName);
      if (name && !this.byName.has(name)) this.byName.set(name, item);
      // Index by article code (the price list's own productCode) so MPC can be
      // resolved directly once the article code is known.
      const articleCode = (item.productCode || '').trim();
      if (articleCode && !this.byArticleCode.has(articleCode)) {
        this.byArticleCode.set(articleCode, item);
      }
    }

    this.cachedAllItems = Array.from(this.byArticleCode.values());
    this.loadedForClient = clientID;
  }

  reset(): void {
    this.byBarcode = new Map();
    this.byCode = new Map();
    this.byName = new Map();
    this.byArticleCode = new Map();
    this.cachedAllItems = [];
    this.loadedForClient = null;
  }

  /**
   * Resolve the article code (PriceList.productCode) for a line item.
   * Matching priority: barcode, then product code, then product name.
   * Returns '' when nothing matches.
   */
  lookupArticleCode(item: {
    barcode?: string;
    productCode?: string;
    productName?: string;
  }): string {
    const matched = this.lookupArticle(item);
    return matched?.productCode || '';
  }

  /** Internal: returns the full PriceListModel that matches, or null.
   *
   * A barcode is only trusted when it is an exact price-list hit. Scanned
   * invoices come back with transposed digits often enough that a barcode
   * failing its check digit must not drive the match — it is repaired against
   * the price list when unambiguous, and otherwise skipped so the product code
   * and name still get their turn.
   *
   * Keep in sync with lookupArticle() in folderInvoicesExportService.
   */
  private lookupArticle(item: {
    barcode?: string;
    productCode?: string;
    productName?: string;
  }): PriceListModel | null {
    const barcode = this.normalizeBarcode(item?.barcode);
    if (barcode) {
      if (this.byBarcode.has(barcode)) return this.byBarcode.get(barcode)!;
      if (this.isValidGtin(barcode) === false) {
        const repaired = this.repairBarcode(barcode);
        if (repaired) return this.byBarcode.get(repaired)!;
      }
    }

    const code = this.normalizeProductCode(item?.productCode);
    if (code && this.byCode.has(code)) return this.byCode.get(code)!;

    const name = this.normalizeProductName(item?.productName);
    if (name && this.byName.has(name)) return this.byName.get(name)!;

    return null;
  }

  /**
   * MPC for one line item, matching barcode, then product code, then name.
   * Returns null when nothing matches.
   */
  lookupRetailPrice(item: {
    barcode?: string;
    productCode?: string;
    productName?: string;
  }): number | null {
    const matched = this.lookupArticle(item);
    if (matched) {
      const price = Number(matched.retailPrice);
      if (!isNaN(price)) return price;
    }
    return null;
  }

  /**
   * MPC for a known article code. This is the primary path once the article
   * code has been resolved (either automatically or selected by the user).
   * Returns null when the article code is not in the price list.
   */
  lookupRetailPriceByArticleCode(articleCode: string | null | undefined): number | null {
    if (!articleCode) return null;
    const matched = this.byArticleCode.get(articleCode.trim());
    if (matched) {
      const price = Number(matched.retailPrice);
      if (!isNaN(price)) return price;
    }
    return null;
  }

  /** The 2-decimal string the templates and the export service both use.
   *
   * The price list stores numbers, but the Bosnian accounting format uses a
   * comma as the decimal separator (1,30 instead of 1.30). The original
   * uploaded format is preserved by converting the dot from toFixed() back
   * to a comma, matching the format the user sees on the invoice itself. */
  formatRetailPrice(price: number | null): string {
    if (price === null || price === undefined || isNaN(Number(price))) return '';
    return Number(price).toFixed(2).replace('.', ',');
  }

  /** Convenience wrapper: resolved MPC as a formatted string, or '' if unmatched. */
  lookupFormatted(item: {
    barcode?: string;
    productCode?: string;
    productName?: string;
  }): string {
    return this.formatRetailPrice(this.lookupRetailPrice(item));
  }

  /** Convenience wrapper: MPC as a formatted string for a known article code. */
  lookupFormattedByArticleCode(articleCode: string | null | undefined): string {
    return this.formatRetailPrice(this.lookupRetailPriceByArticleCode(articleCode));
  }
}
